import { getSignUrl } from '@/service/audioList';
import { AudioDetailDto } from '@/service/interface';
import { AudioDto } from '@/service/type';
import { iOSSafari } from '@/utils/hls';
import { playMediaElement } from '@/utils/media';
import { useWavesurfer } from '@wavesurfer/react';
import Hls from 'hls.js';
import { toast } from 'sonner';
import { create } from 'zustand';

export type PLAY_MODE = 'ONCE' | 'REPEAT' | 'CYCLE';
export const playModes: PLAY_MODE[] = ['ONCE', 'REPEAT', 'CYCLE']; // for mode change order
type ORDER_ENUM = 'PRE' | 'NEXT';

type WaveType = ReturnType<typeof useWavesurfer>;

export type UploadAudio = {
  url: string;
  name: string;
  id?: string;
  duration?: number;
};

type AudioInfoInstance = AudioDto | AudioDetailDto | null | UploadAudio;
type AudioInfoInstanceWithoutNull = Exclude<AudioInfoInstance, null>;

export interface StoreState {
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  play: (audioInfo: AudioInfoInstance, playList?: AudioInfoInstanceWithoutNull[]) => void;
  pause: () => void;
  stop: () => void;
  playList: AudioInfoInstanceWithoutNull[];
  playMode: PLAY_MODE;
  setPlayMode: (playMode: PLAY_MODE) => void;
  playAudioInfo: AudioInfoInstance;
  duration?: number;
  setPlayAudioInfo: (playAudioInfo: AudioDto | null) => void;
  wave: WaveType | null;
  setWave: (wave: WaveType | null) => void;
  currentTime: number;
  setCurrentTime: (currentTime: number) => void;
  getPlayIndex: (order: ORDER_ENUM, isAutoFinish?: boolean) => number | undefined;
  onPre: () => void;
  onNext: (isAutoFinish?: boolean) => void;
  onAudioFinish: () => void;
  playOrPause: (audioInfo: AudioInfoInstance, playList?: AudioInfoInstanceWithoutNull[]) => void;
}

export const useAudioPlayerStore = create<StoreState>((set, get) => {
  return {
    isPlaying: false,
    setIsPlaying: (isPlaying: boolean) => set({ isPlaying }),
    isLoading: false,
    setIsLoading: (isLoading: boolean) => set({ isLoading }),
    playId: '',
    play: async (audioInfo: AudioInfoInstance, playList?: AudioInfoInstanceWithoutNull[]) => {
      if (!audioInfo) {
        return;
      }
      const { playAudioInfo, wave, setIsLoading } = get();
      const currentPlayId = playAudioInfo?.id;
      const playId = audioInfo.id;

      try {
        // use old waveinstance when id is same
        if (playId && playId === currentPlayId) {
          wave?.wavesurfer?.play();
          return;
        }
        if (playList) {
          set({ playList });
        } else {
          set({
            playList: [audioInfo],
          });
        }
        set({ playAudioInfo: audioInfo });
        setIsLoading(true);
        const url =
          (audioInfo && 'url' in audioInfo ? audioInfo.url : null) ||
          (playId &&
            (await getSignUrl({ id: playId }).then(({ data }) => {
              return data?.sign_url;
            })));
        // const url = 'http://jen-local.futureverseai.com/api/v2/audio/123.m3u8';
        // const url = 'https://jen-dev.futureverseai.com/cdn/24_11_18/3e42ca96-4fd7-434c-b720-421c8cc4b447.wav'
        const mediaElement = wave?.wavesurfer?.getMediaElement();

        if (url?.endsWith('.m3u8')) {
          if (iOSSafari) {
            url && mediaElement && (await playMediaElement(mediaElement, url));
            return;
          }
          if (Hls.isSupported()) {
            const hls = new Hls({
              maxBufferLength: 120,
            });
            if (mediaElement) {
              hls.loadSource(url);
              hls.attachMedia(mediaElement);
              // onLoadHlsProgress(hls, (percentage) => {
              //   console.log('percentage', percentage);
              // });
              const loadHls = async () => {
                return new Promise<void>((resolve, reject) => {
                  // trigger when one fragment is loaded
                  hls.on(Hls.Events.FRAG_LOADED, () => {
                    resolve();
                  });
                  hls.on(Hls.Events.ERROR, (_, data) => {
                    if (data?.buffer === 0) {
                      if (hls.media) {
                        const currentTime = hls.media.currentTime || 0;
                        // jump to next chunk
                        hls.media.currentTime = currentTime + 10;
                      }
                    }
                    reject(new Error(`HLS Error: ${data?.error}`));
                  });
                });
              };
              await loadHls();
              mediaElement?.play();
              setIsLoading(false);
            }
          }
        } else {
          if (url && mediaElement) {
            const { duration } = await playMediaElement(mediaElement, url);
            set({ duration: duration });
          }
        }
      } catch {
        // todo: add monitor to report songId & url
        set({ playAudioInfo: null });
        set({ playList: [] });
        toast.error('Playing track error');
      } finally {
        setIsLoading(false);
      }
    },
    duration: 60,
    pause: () => {
      const { wave } = get();
      wave?.wavesurfer?.pause();
    },
    stop: () => {
      const { wave, setCurrentTime } = get();
      wave?.wavesurfer?.stop();
      setCurrentTime(0);
    },
    playOrPause: (audioInfo: AudioInfoInstance, playList?: AudioInfoInstanceWithoutNull[]) => {
      const { isPlaying, play, pause, playAudioInfo } = get();
      if (isPlaying && playAudioInfo?.id === audioInfo?.id) {
        pause();
      } else {
        play(audioInfo, playList);
      }
    },

    playList: [],
    playMode: 'ONCE',
    setPlayMode: (playMode: PLAY_MODE) => {
      set({ playMode });
    },
    playAudioInfo: null,
    setPlayAudioInfo: (playAudioInfo: AudioDto | null) => {
      set({ playAudioInfo });
    },
    wave: null,
    setWave: (wave: WaveType | null) => {
      set({ wave });
    },

    currentTime: 0,
    setCurrentTime: (currentTime: number) => {
      set({ currentTime });
    },
    getPlayIndex: (order: ORDER_ENUM, isAutoFinish = false) => {
      const { playMode, playList, playAudioInfo } = get();
      const currentPlayIndex = playList.findIndex(({ id }) => {
        return id === playAudioInfo?.id;
      });

      if (playMode === 'CYCLE' || !isAutoFinish) {
        if (order === 'NEXT') {
          return currentPlayIndex + 1 >= playList.length ? 0 : currentPlayIndex + 1;
        } else {
          return currentPlayIndex - 1 < 0 ? playList.length - 1 : currentPlayIndex - 1;
        }
      }

      if (playMode === 'ONCE') {
        return;
      }

      if (playMode === 'REPEAT') {
        return currentPlayIndex;
      }
    },

    // 上一首
    onPre: () => {
      const { getPlayIndex, playList, play } = get();
      const nextPlayIndex = getPlayIndex('PRE');
      if (nextPlayIndex !== undefined && playList.length) {
        const { id } = playList[nextPlayIndex];
        play(playList[nextPlayIndex], playList);
      }
    },

    // 下一首
    onNext: (isAutoFinish) => {
      const { getPlayIndex, playList, play } = get();
      const nextPlayIndex = getPlayIndex('NEXT', isAutoFinish);
      if (nextPlayIndex !== undefined && playList.length) {
        const { id } = playList[nextPlayIndex];
        play(playList[nextPlayIndex], playList);
      }
    },

    // 播放结束后触发
    onAudioFinish: () => {
      const { onNext } = get();
      onNext(true);
    },
  };
});
